import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { matchPath } from 'react-router-dom';
import { History } from 'history';
import { AppRoute } from './core';

type GetProps = {
  isEnabledInProduction: boolean;
  isEnabledInDev: boolean;
};

const getDSN = ({ isEnabledInProduction, isEnabledInDev }: GetProps): string => {
  if (isEnabledInProduction) {
    return window.env ? window.env.SENTRY_DSN_URL : '';
  }

  if (isEnabledInDev) {
    return import.meta.env.REACT_APP_SENTRY_DSN_URL || '';
  }
  return '';
};

const getEnvironment = ({ isEnabledInProduction, isEnabledInDev }: GetProps): string => {
  if (isEnabledInProduction) {
    // Injected from GitHub Workflow
    return import.meta.env.REACT_APP_SENTRY_ENVIRONMENT || 'undefined';
  }

  if (isEnabledInDev) {
    return 'dev';
  }
  return '';
};

export type InitSentryProps = {
  history: History;
  routes: AppRoute[];
};

export const initSentry = ({ history, routes }: InitSentryProps): void => {
  // Integration, Staging or Production
  const isEnabledInProduction = Boolean(
    window.env && window.env.SENTRY_DSN_URL && import.meta.env.REACT_APP_SENTRY_ENVIRONMENT
  );
  // See README on how to enable Sentry locally
  const isEnabledInDev = import.meta.env.REACT_APP_SENTRY_ENABLE_IN_DEV === '1';

  if (isEnabledInProduction || isEnabledInDev) {
    const dsn = getDSN({ isEnabledInDev, isEnabledInProduction });
    const environment = getEnvironment({ isEnabledInDev, isEnabledInProduction });

    // Injected from GitHub Workflow
    const release = import.meta.env.REACT_APP_SENTRY_RELEASE_VERSION;

    Sentry.init({
      dsn,
      environment,
      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV5Instrumentation(history, routes, matchPath),
        }),
      ],
      release,
      tracesSampleRate: 1.0,
    });
  }
};
